// This is the main Store for the Buyflow section of the site. It is the parent store for all the other stores in this directory.
import axios from "axios";

// Helpers
let timeout
import {DateTime as dt} from 'luxon'

// Factories
import finalizeFactory from "../factories/finalizeFactory";

// Stores
import tv from './tv'
import voice from './voice'
import receipt from './receipt'
import summary from './summary'
import internet from './internet'
import finalize from './finalize'
import tracking from './tracking'

// Actions
import bridge from './bridge'

export default {
    namespaced: true,

    state: () => ({
        source: null, // current axios call with the API (used for cancelling requests)
        decisions: {}, // the decisions object of all the selections made by the user
        isReady: false, // when all data is loaded and the page can be displayed
        currentStep: 0, // the current step in the checkout process (0 = check availability, 1 = internet, 2 = tv, 3 = voice, 4 = finalize, 5 = summary/order complete)
        apiRequestDelay: 1000, // this is the minimum delay between api requests in milliseconds
        fromMobileLP: false, // if the user originally came from the mobile landing page
        mobileEligible: false, // if the user is eligible for mobile
        mobileInterested: false, // if the user is interested in mobile
        hasGiftCard: false,
        session: {
            dateTime: null, // start time
            lastActive: null, // last time the user was active
            duration: 20, // how long in minutes from the dateTime till the session will expire
            expiryWarning: 15, // how long in minutes from the dateTime till the session inactive popup will show if the user is still active
            activeDuration: 5, // how long in minutes from the lastActive till the session inactive popup will show
            showNotification: false,
            hasCompletedOrder: false,
            returnToUrl: '/',
        },
    }),

    getters: {
        isOhio (state, getters, rootState) {
            return rootState.meta.stateOrProvince === 'OH'
        },
        isWowMarket (state, getters, rootState) {
            return rootState.meta.marketType === 'ohio'
        },
        sectionStatus: (state, getters) => (flag, type) => {
            return getters['buyflowFeature'](flag,type)
        },
        formatDecisions: () => (payload) => { // formats the decisions object to match the expected format
            let params = {}

            // format the payload to match the expected format
            Object.keys(payload).forEach(key => {
                if (payload[key]?.decision) {
                    if (payload[key]?.selection !== null && payload[key]?.quantity !== undefined) {
                        params[payload[key]?.decision] = {
                            name: payload[key]?.selection?.name,
                            quantity: payload[key]?.quantity
                        }
                        return
                    } else if (payload[key]?.selection !== null && Array.isArray(payload[key]?.selection)) {

                        params[payload[key]?.decision] = payload[key]?.selection.map(item => ({
                            name: item.name,
                        }))
                        return
                    } else if (payload[key]?.selection !== null) {
                        params[payload[key]?.decision] = {
                            name: payload[key]?.selection?.name
                        }
                        return
                    }
                    params[payload[key]?.decision] = null
                }
            })

            /**
             * Expected params format:
             * {
             *    "HS1 HSD Service": {
             *      "name": "Unlimited GigaFast"
             *    },
             *    "decision name": {
             *      "name": "option name"
             *    }
             * }
             */
            return params
        },
        buyflowFeature: (state, getters, rootState, rootGetters) => (flag, type) => {
            // the feature flag we may be looking for:
            // offer_3665|buyflow_hide=tv:video:edits|buyflow_minimal=internet
            //
            // if there is a buyflow related feature flag that we are looking for on the location
            const feature = rootState.meta.features?.filter(f => f.indexOf(flag) > -1)|| []

            // if feature is not empty
            for (let i in feature){
                if(rootGetters['offerIdMatches'](feature[i])){
                    const items = feature[i].split('|')
                    for (let i in items){
                        // if flag = buyflow_hide
                        // return this section tv:video:edits
                        if(items[i].indexOf(flag) > -1) {
                            // feature is active
                            return ((items[i].split('=')[1] || '').indexOf(type) > -1)
                        }
                    }
                }
            }
            // Feature is not active
            return false
        },
        cartUrl(state, getters, rootState) {
            if (!rootState.meta.uuid) {
                return null
            }

            const url = `${window.location.origin}/checkout/internet`
            const params = new URLSearchParams()
            params.append('uuid', rootState.meta.uuid)
            params.append('ref', 'abandonedcart')

            // if (rootState.meta.abandonedCart.giftCardTrigger) {
            //     params.append(rootState.meta.abandonedCart.giftCardTrigger, true)
            // }

            return `${url}?${params}`
        }
    },

    mutations: {
        PRELOAD (state, payload) {
            state.internet.data              = { ...payload.internet }
            state.tv.data                    = { ...payload.tv }
            state.voice.data                 = { ...payload.voice }
            state.finalize.installData       = { ...payload.install }
            state.finalize.scheduleTimes     = []
            state.finalize.confirmedDateTime = null
        },
        SET_READY (state) {
            state.isReady = true
        },
        START_SESSION (state, dateTime) {
            state.session.dateTime = dateTime
        },
        END_SESSION (state) {
            state.session.showNotification = true
            state.session.dateTime = null
            window.location.replace(state.session.returnToUrl || '/')
        },
        UPDATE_LAST_ACTIVE (state, dateTime) {
            state.session.lastActive = dateTime
        },
        SET_RETURN_URL(state, payload){
            state.session.returnToUrl = payload
        },
        CLEAR_EXPIRED_SESSION_NOTIFICATION (state) {
            state.session.showNotification = false
        },
        SET_CURRENT_STEP(state, payload){
            state.currentStep = payload
        },
        SET_SOURCE(state, payload){
            state.source = payload
        },
        UPDATE_MOBILE_ELIGIBLE(state, value) {
            state.mobileEligible = value
        },
        UPDATE_MOBILE_INTERESTED(state, value) {
            state.mobileInterested = value
        },
        SET_DECISIONS (state, payload) {
            state.decisions = payload
        },
        UPDATE_DECISIONS (state, payload) {
            state.decisions = {...state.decisions, ...payload}
        },
        COMPLETE_ORDER (state, payload = true) {
            state.session.hasCompletedOrder = payload
        },
        SET_HAS_GIFT_CARD(state, payload) {
            state.hasGiftCard = payload
        },
    },

    actions: {
        ...bridge, // this contains most/all the connections between the buyflow and the API
        initialize ({ dispatch, commit, state }) {
            return dispatch('_fetchCache')
                .then(response => {
                    commit('PRELOAD', response)
                    return dispatch("_fetchTaxes")
                        .then(futureStatement => dispatch('receipt/updateCart', {
                            ...state.receipt.cartData,
                            taxes:futureStatement.taxFees,
                        }))
                })
                .catch(e => {
                    console.error(e)
                    dispatch('togglePopup', {
                        type: 'COMMON/ERRORS/UNEXPECTED',
                        error: e,
                    }, ROOT)
                })
                .finally(() => {
                    dispatch('startSession')
                })
        },
        reload ({ dispatch, commit, getters } ) {
            return dispatch('internet/init')
                .then(() => dispatch('tv/init'))
                .then(() => dispatch('voice/init'))
                .then(() => dispatch('updateDecisions', {}))
                .finally(() => {
                    commit('SET_READY')
                    dispatch('startSession')
                })
        },
        startSession ({ commit }) {
            commit('START_SESSION', dt.now().toISO())
        },
        setActive ({ commit }) {
            commit('UPDATE_LAST_ACTIVE', dt.now().toISO())
        },
        setReturnUrl ({ commit }, payload = '/') {
            commit('SET_RETURN_URL', payload)
        },
        setSessionStatus ({ state, dispatch, rootState }, payload) {
            // Get label data from the store
            const labels = rootState.BroadbandLabels.labelData || null
            return dispatch('_setSessionStatus', { ...payload, mobile_eligible: state.mobileEligible, mobile_interested: state.mobileInterested, label_data: labels })
        },
        retrieveUserSession({ dispatch }){
            return dispatch('_fetchCustomerInformation')
        },
        endSession ({ commit }) {
            commit('RESET_PERSISTED_SESSION', {}, ROOT)
            commit('RESET_LABELS', null, ROOT)
            commit('END_SESSION')
        },
        setCurrentStep({ commit, rootState }, payload) {
            dataLayer.push({
                cookie_state: rootState.meta.stateOrProvince || null,
                cookie_zip_code: rootState.meta.postalCode ? rootState.meta.postalCode.substring(0, 5) : null,
            })

            gtag('event', 'checkout_progress', {
                checkout_step: payload[0],
                items: [ rootState.ecommerceItem, ],
            });

            commit('SET_CURRENT_STEP', payload)
        },
        clearExpiredSessionNotification ({ commit }) {
            commit('CLEAR_EXPIRED_SESSION_NOTIFICATION')
        },
        setMobileEligibility({ commit }, value) {
            commit('UPDATE_MOBILE_ELIGIBLE', value)
        },
        setMobileInterested({ commit }, value) {
            commit('UPDATE_MOBILE_INTERESTED', value)
        },
        updateDecisions ({ dispatch, commit, state, getters }, payload) {
            console.log("update decisions: ", typeof payload, JSON.stringify(payload, null, 2))
            // cancel any pending requests
            if (state.source){
                state.source.cancel("cancelled previous update decisions request")
            }
            commit('SET_SOURCE', axios.CancelToken.source())

            // Delay Override
            let delay = state.apiRequestDelay
            if (payload?.delay !== undefined) {
                delay = payload.delay
                delete payload.delay
            }

            // format and commit the new decisions to the existing decisions
            const params = getters.formatDecisions(payload)
            commit('UPDATE_DECISIONS', params)

            // Clear the finalization progression if the change is not an installation change
            if (!Object.keys(params).every(key => key === 'IF1 Install - Fulfillment')) {
                dispatch('finalize/setFinalizeState', finalizeFactory())
            }

            // Clear the previous timeout if it exists
            if (timeout) clearTimeout(timeout);

            // set the skeleton loading state of the receipt
            dispatch('receipt/setReceiptLoading', true)

            // Set up the new timeout which prevents the decisions from being updated too frequently and CSG from being overloaded
            if (delay !== 0) {
                timeout = setTimeout(() => {
                    return dispatch('updateDecisionsCallback')
                }, delay);
            } else {
                return dispatch('updateDecisionsCallback')
            }
        },
        updateDecisionsCallback ({ dispatch, commit, state }) {
            return dispatch('_all_updateDecisions', state.decisions)
                .then(response => {
                    dispatch("_formatCartData", response.data.data.receipt)
                        .then((cartData) => {
                            dispatch("_fetchTaxes")
                                .then(futureStatement => dispatch('receipt/updateCart', {
                                    ...cartData,
                                    taxes:futureStatement.taxFees,
                                }))
                        })

                        commit("SET_DECISIONS", Object.fromEntries(
                            Object.entries(
                                response?.data?.meta?.decisionTree ?? {}
                            ).filter(([_, v]) => v != null))
                        )
                })
                .then(() => dispatch('tracking/updatePersonalizationCart', false))
                .catch(e => {
                    if (e?.message !== "cancelled previous update decisions request") {
                        console.error(e)
                        dispatch('togglePopup', {
                            type: 'COMMON/ERRORS/UNEXPECTED',
                            error: e,
                        }, ROOT)
                        // deactivate the skeleton loading state of the receipt
                        dispatch('receipt/setReceiptLoading', false)
                    }
                }).finally(() => {
                    if (state.isReady) {
                        dispatch('startSession')
                    }
                })
        },
        addPromotion({ dispatch },  promoCode) {
            const decision = {
                promo: {
                    decision: "WP1 Web Promo",
                    selection: { name: promoCode },
                },
                delay: 1,
            }

            return dispatch('updateDecisions', decision);
        },
    },

    modules: {
        tv,
        voice,
        receipt,
        internet,
        finalize,
        tracking,
        summary
    },
}
